import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import Dropdown from '../../../../../components/Dropdown/Dropdown';
import { IDropdownOption } from '../../../../../components/Dropdown/IDropdown';
import Input from '../../../../../components/Input/Input';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel } from '../../../../conversion';
import { ISectorResponse } from '../../../api/IApiResponse';
import { ChannelType, SectorInclination } from '../../../enum/Sector';
import { selectProject } from '../../../selector/conversion';
import { setSector } from '../../../store/project/projectSlice';
import { cleanResults, setDischargeAlert } from '../../../store/project/resultsSlice';
import * as utils from '../../../utils/utils';
import './Geometry.scss';
import { IGeometryProps } from './IGeometry';
import logoGeomtery from './images/geometry.png';
import { MAX_ANGLE, handleChangeInclination } from './utils';
import { IMaterialResponse } from '../../../../api/IApiResponse';
import { selectProjectInfo } from '../../../../store/projectInfo/projectInfoSlice';
import { fetchMaterialsByType, selectMaterial } from '../../../../store/material/materialSlice';
import { MaterialType } from '../../../../enum/Material';

export interface IGeometryForm {
    sector: number;
    inclination: SectorInclination;
    height: number;
    width: number;
    angle: number;
    length: number;
    channelType: ChannelType;
    material?: IMaterialResponse;
    isActive: boolean;
}

const inclinationOptions: IDropdownOption[] = [
    { label: '1:1', value: SectorInclination.OneToOne },
    { label: '1.5:1', value: SectorInclination.OnePointFiveToOne },
    { label: '1.75:1', value: SectorInclination.OnePointSeventFiveToOne },
    { label: '2:1', value: SectorInclination.TwoToOne },
    { label: '3:1', value: SectorInclination.ThreeToOne },
    { label: '2:3', value: SectorInclination.TwoToThree },
    { label: '90°', value: SectorInclination.NinetyDegrees },
    { label: 'Gabion 6°', value: SectorInclination.GabionSixDegrees },
    { label: 'Plain', value: SectorInclination.Plain },
    { label: 'By angle', value: SectorInclination.ByAngle },
];

const Geometry = (props: IGeometryProps) => {
    const { t } = useTranslation();
    const project = useAppSelector(selectProject);
    const projectInfo = useAppSelector(selectProjectInfo);
    const material = useAppSelector(selectMaterial);
    const dispatch = useAppDispatch();

    const [selectedSector, setSelectedSector] = useState<ISectorResponse>();
    const [sectorOptions, setSectorOptions] = useState<IDropdownOption[]>([]);

    let timer = React.useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let sector: ISectorResponse | any = {};
        if (!!project.data && !!project.data.geometry?.length) {
            sector = project.data.geometry[0];
            setSelectedSector(sector);
        }
    }, []);

    useEffect(() => {
        if (!!material.initialState && !material.pending) {
            dispatch(fetchMaterialsByType(MaterialType.ProtectionSystems));
        }
    }, [project.data]);

    const handleSectorChange = useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            let sector: ISectorResponse | any = {};
            if (!!project.data && !!project.data.geometry?.length) {
                let channel = parseInt(e.target.value);
                sector = project.data.geometry.find(sec => sec.channel === channel);
                if (!!sector) setSelectedSector(sector);
                else
                    setSelectedSector({
                        channel,
                        inclination: inclinationOptions[0].value as number,
                        channelType: utils.getChannelTypeById(channel),
                        height: 0,
                        width: 0,
                        angle: 0,
                        length: 0,
                    });
            }
        },
        [project.data],
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | undefined) => {
        if (!!e) {
            let target = e.target;
            let name = target.name;
            let value = parseFloat(target.value);
            if (isNaN(value)) {
                value = 0;
            }
            if (name === 'angle') {
                if (value! > MAX_ANGLE) value = MAX_ANGLE;
            }
            if (!!selectedSector) {
                handleInclinationChange(undefined, {
                    ...selectedSector,
                    [name]: value,
                });
            }
        }
    };

    const handleInclinationChange = useCallback(
        (e?: React.ChangeEvent<HTMLSelectElement>, modifiedSector?: ISectorResponse) => {
            if (!!selectedSector) {
                let inclination: SectorInclination;
                if (!!modifiedSector) {
                    inclination = modifiedSector.inclination;
                }
                if (!!e) {
                    inclination = parseInt(e.target.value);
                }

                let sector = handleChangeInclination({ ...(modifiedSector ?? selectedSector), inclination: inclination! });
                setSelectedSector(sector);
                let defMat = material.data![0];
                if (!!sector) {
                    let payload: IGeometryForm = {
                        sector: sector.channel,
                        inclination: sector.inclination,
                        height: sector.height,
                        width: sector.width,
                        length: sector.length,
                        angle: sector.angle,
                        channelType: utils.getChannelTypeById(sector.channel),
                        material: sector.material ?? defMat!,
                        isActive: sector.length > 0,
                    };

                    if (payload) {
                        if (timer.current) {
                            clearTimeout(timer.current);
                        }
                        timer.current = setTimeout(() => {
                            dispatch(cleanResults());
                            dispatch(setSector(payload));
                            dispatch(setDischargeAlert(false));
                        }, 2000);
                    }
                }
            }
        },
        [selectedSector, material],
    );

    useEffect(() => {
        let filteredOptions: IDropdownOption[] = [];
        if (!!project.data && !!project.data.geometry?.length) {
            project.data.geometry.forEach(sector => filteredOptions.push({ label: `${sector.channel}`, value: sector.channel }));
            filteredOptions.sort((a, b) => (a.value as number) - (b.value as number));
            let firstChannel = filteredOptions[0].value;
            let lastChannel = filteredOptions[filteredOptions.length - 1].value;
            if (firstChannel === 2 || firstChannel === 3) {
                let firstValue = firstChannel - 1;
                filteredOptions.unshift({ label: `${firstValue}`, value: firstValue });
            }
            if (lastChannel === 5 || lastChannel === 6) {
                let lastValue = lastChannel + 1;
                filteredOptions.push({ label: `${lastValue}`, value: lastValue });
            }
            setSectorOptions(filteredOptions);
        }
    }, [project.data]);

    return (
        <div className='geometry'>
            <Dropdown title={t('geometry_sector')} name='sector' className='d-flex flex-column pb-3' options={sectorOptions} defaultValue={selectedSector?.channel} onChange={handleSectorChange} />
            <Tabs>
                <TabList>
                    <Tab>{t('geometry_sector_geometry')}</Tab>
                </TabList>
                <TabPanel>
                    <div className='d-flex justify-content-between'>
                        <Dropdown
                            title={t('geometry_inclination')}
                            name='inclination'
                            className='d-flex flex-column w-50'
                            value={selectedSector?.inclination}
                            options={inclinationOptions}
                            onChange={handleInclinationChange}
                        />
                        <div>
                            <label>{t('geometry_sector')}</label>
                            <h2>{selectedSector?.channel}</h2>
                        </div>
                    </div>

                    <div className='w-100 pt-2'>
                        <div className='d-flex justify-content-between align-items-center'>
                            <Input
                                type='number'
                                step={0.01}
                                value={selectedSector?.height}
                                title={t('geometry_height', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                                name='height'
                                disabled={selectedSector?.inclination === SectorInclination.Plain}
                                className={`unit${selectedSector?.inclination === SectorInclination.Plain ? ' disabled' : ''}`}
                                styleInput={{ width: '-webkit-fill-available' }}
                                onChange={handleInputChange}
                            />
                            <img src={logoGeomtery} alt={t('logo_geometry_image_alt') as string} className='image-div' />
                            <Input
                                type='number'
                                step={0.01}
                                value={selectedSector?.angle}
                                title={t('geometry_angle', { unit_measure: '°' })}
                                name='angle'
                                disabled={selectedSector?.inclination !== SectorInclination.ByAngle}
                                className={`unit${selectedSector?.inclination !== SectorInclination.ByAngle ? ' disabled' : ''}`}
                                styleInput={{ width: '-webkit-fill-available' }}
                                onChange={handleInputChange}
                            />
                        </div>
                        <Input
                            type='number'
                            step={0.01}
                            value={selectedSector?.width}
                            title={t('geometry_width', { unit_measure: conversionLabel.m_ft[projectInfo.data?.unitOfMeasure as number] })}
                            name='width'
                            disabled={selectedSector?.inclination !== SectorInclination.Plain}
                            className={`unit mx-auto${selectedSector?.inclination !== SectorInclination.Plain ? ' disabled' : ''}`}
                            styleInput={{ width: '-webkit-fill-available' }}
                            onChange={handleInputChange}
                        />
                    </div>
                </TabPanel>
            </Tabs>
        </div>
    );
};

export default Geometry;
